import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import logo from "../../assets/logo1.png";
import hiklogo from "../../assets/hikvision.png";
import dahuaLogo from "../../assets/dahuaLogo.png";
import zktlogo from "../../assets/zkteco.png";

// 🔥 FIREBASE IMPORTS
import { db } from "../../utils/firebase";
import { collection, query, where, getDocsFromServer, limit } from "firebase/firestore";

// 🔗 Constants
const LOGO_URL = logo;
const NUMPAD_KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "x", "0", "⌫"];

const SEARCH_TIMEOUT_MS = 5000;
const SEARCH_CACHE_TTL_MS = 15000;

const plateSearchCache = new Map();
const plateSearchInFlight = new Map();

const withTimeout = (promise, timeoutMs, message) => {
    let timer;
    return Promise.race([
        promise,
        new Promise((_, reject) => {
            timer = setTimeout(() => reject(new Error(message)), timeoutMs);
        }),
    ]).finally(() => clearTimeout(timer));
};

// ----------------------------------------------------
// ⚙️ HELPER FUNCTIONS (Logic)
// ----------------------------------------------------

// 1. Firebase-ийн индекст гацахгүй, маш хурдан ажиллах Client-side хайлт
// 1. Firebase-ийн индекст гацахгүй, маш хурдан ажиллах Database-side хайлт
const fetchPlatesByPrefix = async (prefix, parkId) => {
    if (!prefix || prefix.length !== 4 || !parkId) return [];

    const cacheKey = `${parkId}:${prefix}`;
    const now = Date.now();

    const cached = plateSearchCache.get(cacheKey);
    if (cached && now - cached.timestamp < SEARCH_CACHE_TTL_MS) {
        return cached.results;
    }

    if (plateSearchInFlight.has(cacheKey)) {
        return plateSearchInFlight.get(cacheKey);
    }

    const searchPromise = (async () => {
        try {
            const recordsCollectionRef = collection(db, "parkings", parkId, "records");

            const q = query(
                recordsCollectionRef,
                where("isActive", "==", true),
                where("registered", "==", false),
                where("car_plate", ">=", prefix),
                where("car_plate", "<=", prefix + "\uf8ff"),
                limit(200), // Search up to 200 prefix matches; server remains authoritative.

            );

            const snapshot = await withTimeout(
                getDocsFromServer(q),
                SEARCH_TIMEOUT_MS,
                "Дахин оролдоно уу.",
            );

            // IMPORTANT: no first-hit-wins. A plate may have multiple open records.
            // Choose the newest entry_time for display; backend MUST repeat this
            // check transactionally before issuing a payment invoice.
            const newestByPlate = new Map();
            const entryMillis = (value) =>
                value?.toMillis?.() ??
                (typeof value?.seconds === "number" ? value.seconds * 1000 : Date.parse(value));
            snapshot.docs.forEach((docSnap) => {
                const data = docSnap.data();
                const plate = String(data.car_plate || "");
                const entryTimeMs = entryMillis(data.entry_time);
                if (!plate || !Number.isFinite(entryTimeMs)) return;
                const previous = newestByPlate.get(plate);
                if (!previous || entryTimeMs > previous.entryTimeMs) {
                    newestByPlate.set(plate, {
                        recordId: docSnap.id,
                        plate,
                        entryTime: data.entry_time,
                        entryTimeMs,
                        isLarge: data.isLarge || false,
                        invited: data.invited === true,
                        ambiguous: false,
                    });
                } else if (entryTimeMs === previous.entryTimeMs && previous.recordId !== docSnap.id) {
                    newestByPlate.set(plate, { ...previous, ambiguous: true });
                }
            });
            const sortedResults = [...newestByPlate.values()].sort((a, b) => a.plate.localeCompare(b.plate));

            plateSearchCache.set(cacheKey, {
                timestamp: Date.now(),
                results: sortedResults,
            });

            return sortedResults;
        } catch (error) {
            if (error?.message?.includes("Дахин оролдоно уу.")) {
                throw error;
            }

            throw new Error("Өгөгдөл татахад алдаа гарлаа. Сүлжээгээ шалгана уу.");
        } finally {
            plateSearchInFlight.delete(cacheKey);
        }
    })();

    plateSearchInFlight.set(cacheKey, searchPromise);
    return searchPromise;
};

// ----------------------------------------------------
// 🖼️ MINIMAL PREMIUM MODAL
// ----------------------------------------------------

const UI_FONT = '"Noto Sans", Inter, "Segoe UI", Arial, sans-serif';

const PlateOptionsModal = ({ plates, onSelect, onClose }) => {
    // Nothing is preselected. A deliberate confirmation is required before any payment.
    const [selectedRecordId, setSelectedRecordId] = useState(null);
    const [selectionError, setSelectionError] = useState("");
    const [continuing, setContinuing] = useState(false);

    const selectPlate = (recordId) => {
        setSelectedRecordId(recordId);
        setSelectionError("");
    };

    const handleContinue = () => {
        if (continuing) return;
        const selectedRecord = plates.find((record) => record.recordId === selectedRecordId);
        if (!selectedRecord) {
            setSelectionError("Та өөрийн машины дугаарыг сонгоно уу");
            return;
        }
        setContinuing(true);
        onSelect(selectedRecord);
        // If the parent rejects an ambiguous entry, keep this dialog usable.
        setContinuing(false);
    };

    useEffect(() => {
        const onKeyDown = (event) => {
            if (event.key === "Escape") onClose();
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [onClose]);

    return (
        <div
            className="tw-fixed tw-inset-0 tw-z-[100000] tw-flex tw-items-end tw-justify-center tw-bg-[#061321]/65 tw-p-0 tw-backdrop-blur-[6px] sm:tw-items-center sm:tw-p-4"
            onClick={onClose}
            style={{ fontFamily: UI_FONT }}
        >
            <section
                role="dialog"
                aria-modal="true"
                aria-labelledby="iparking-plate-title"
                onClick={(event) => event.stopPropagation()}
                className="tw-flex tw-max-h-[94vh] tw-w-full tw-max-w-[440px] tw-flex-col tw-overflow-hidden tw-rounded-t-[28px] tw-bg-white tw-shadow-[0_30px_90px_rgba(5,23,34,0.3)] sm:tw-max-h-[88vh] sm:tw-rounded-[28px]"
            >
                <div className="tw-shrink-0 tw-border-b tw-border-slate-100 tw-bg-[#F8FAFA] tw-px-5 tw-pt-5 tw-pb-4 sm:tw-px-6">
                    <div className="tw-flex tw-items-start tw-justify-between tw-gap-4">
                        <div className="tw-min-w-0">
                            <div className="tw-flex tw-items-center tw-gap-2">
                                <span className="tw-inline-flex tw-h-7 tw-items-center tw-rounded-full tw-bg-[#DDF5EC] tw-px-3 tw-text-[10px] tw-font-bold tw-text-[#087457]">
                                    АЛХАМ 2 / 3
                                </span>
                                <span className="tw-text-[11px] tw-font-medium tw-text-slate-500">
                                    {plates.length} илэрц
                                </span>
                            </div>
                            <h2 id="iparking-plate-title"  className=" tw-mt-3 tw-mb-1 tw-text-[15px] tw-font-bold  tw-text-[#102A32] ">
                                Та өөрийн машины дугаарыг сонгоно уу
                            </h2>

                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            aria-label="Хаах"
                            className="tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-slate-200 tw-bg-white tw-text-slate-500 tw-transition hover:tw-bg-slate-100 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-[#12A87A]"
                        >
                            <svg viewBox="0 0 24 24" className="tw-h-5 tw-w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                                <path d="M6 6l12 12M18 6L6 18" />
                            </svg>
                        </button>
                    </div>
                </div>

                <div className="tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-bg-white tw-px-4 tw-py-4 sm:tw-px-5">
                    {plates.length > 0 ? (
                        <div className="tw-space-y-2.5" role="group" aria-label="Улсын дугаарын жагсаалт">
                            {plates.map((record) => {
                                const isSelected = selectedRecordId === record.recordId;
                                return (
                                    <button
                                        type="button"
                                        key={record.recordId}
                                        onClick={() => selectPlate(record.recordId)}
                                        aria-pressed={isSelected}
                                        className={`tw-flex tw-min-h-[82px] tw-w-full tw-items-center tw-gap-3 tw-rounded-[18px] tw-border-2 tw-px-4 tw-py-3 tw-text-left tw-transition-all tw-duration-150 active:tw-scale-[0.99] focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-[#A5EBD1] ${isSelected
                                            ? "tw-border-[#12A87A] tw-bg-[#EDFBF5] tw-shadow-[0_4px_14px_rgba(18,168,122,0.1)]"
                                            : "tw-border-[#E9EFF0] tw-bg-white hover:tw-border-[#9FDCC6] hover:tw-bg-[#FAFEFC]"}`}
                                    >
                                        <span className={`tw-flex tw-h-11 tw-w-11 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-[14px] ${isSelected ? "tw-bg-[#D9F6E8] tw-text-[#087457]" : "tw-bg-[#F1F5F6] tw-text-[#526874]"}`}>
                                            <svg viewBox="0 0 24 24" className="tw-h-6 tw-w-6" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M5 17h14M7 17l-1 2M17 17l1 2M6 13l1.2-4.1A2 2 0 0 1 9.1 7.5h5.8a2 2 0 0 1 1.9 1.4L18 13" />
                                                <path d="M5 13h14v4H5z" />
                                            </svg>
                                        </span>
                                        <span className="tw-min-w-0 tw-flex-1">
                                            <span className="tw-block tw-truncate tw-text-[23px] tw-font-extrabold tw-tracking-[0.055em] tw-text-[#102A32]">
                                                {record.plate}
                                            </span>
                                         
                                        </span>
                                        <span className={`tw-flex tw-h-7 tw-w-7 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border-2 ${isSelected ? "tw-border-[#0B9470] tw-bg-[#0B9470] tw-text-white" : "tw-border-[#C8D3D8] tw-bg-white"}`}>
                                            {isSelected && (
                                                <svg viewBox="0 0 24 24" className="tw-h-4 tw-w-4" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M5 12l4 4L19 6" />
                                                </svg>
                                            )}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="tw-flex tw-min-h-[190px] tw-flex-col tw-items-center tw-justify-center tw-p-5 tw-text-center">
                            <div className="tw-flex tw-h-14 tw-w-14 tw-items-center tw-justify-center tw-rounded-[20px] tw-bg-[#F1F5F6] tw-text-[#647785]">
                                <svg viewBox="0 0 24 24" className="tw-h-7 tw-w-7" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                                    <circle cx="11" cy="11" r="7"/><path d="m20 20-4-4"/>
                                </svg>
                            </div>
                            <p className="tw-mt-4 tw-mb-1 tw-text-[17px] tw-font-bold tw-text-[#102A32]">Дугаар олдсонгүй</p>
                            <p className="tw-m-0 tw-text-[12px] tw-leading-5 tw-text-slate-500">Дугаараа шалгана уу.</p>
                        </div>
                    )}
                </div>

                <div className="tw-shrink-0 tw-border-t tw-border-[#EDF1F2] tw-bg-white tw-px-4 tw-pt-3 tw-pb-5 sm:tw-px-5 sm:tw-pb-4">
                    {selectionError ? (
                        <div role="alert" aria-live="assertive" className="tw-mb-3 tw-flex tw-items-center tw-gap-2 tw-rounded-[14px] tw-border tw-border-amber-200 tw-bg-amber-50 tw-px-3 tw-py-3 tw-text-[12px] tw-font-semibold tw-text-amber-950">
                            <svg viewBox="0 0 24 24" className="tw-h-5 tw-w-5 tw-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                                <circle cx="12" cy="12" r="10"/><path d="M12 7v6M12 17h.01"/>
                            </svg>
                            {selectionError}
                        </div>
                    ) : (
                        <p className="tw-m-0 tw-mb-3 tw-text-center tw-text-[11px] tw-text-slate-500">
                            {selectedRecordId ? "" : ""}
                        </p>
                    )}

                    {plates.length > 0 ? <button
                        type="button"
                        onClick={handleContinue}
                        disabled={continuing}
                        className="tw-flex tw-h-[54px] tw-w-full tw-items-center tw-justify-center tw-gap-2 tw-rounded-[16px] tw-bg-[#07966E] tw-text-[15px] tw-font-bold tw-text-white tw-shadow-[0_10px_24px_rgba(7,150,110,0.21)] tw-transition hover:tw-bg-[#057C5B] active:tw-scale-[0.99] focus:tw-outline-none focus:tw-ring-4 focus:tw-ring-[#ACEDDA] disabled:tw-opacity-60"
                    >
                        {continuing ? "Шилжиж байна..." : "Үргэлжлүүлэх"}
                        <svg viewBox="0 0 24 24" className="tw-h-5 tw-w-5" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M5 12h14m-6-6 6 6-6 6" />
                        </svg>
                    </button> : <button
                        type="button"
                        onClick={onClose}
                        disabled={continuing}
                        className="tw-flex tw-h-[54px] tw-w-full tw-items-center tw-justify-center tw-gap-2 tw-rounded-[16px] tw-bg-red-500 tw-text-[15px] tw-font-bold tw-text-white tw-shadow-[0_10px_24px_rgba(7,150,110,0.21)] tw-transition hover:tw-bg-red-600 active:tw-scale-[0.99] focus:tw-outline-none focus:tw-ring-4 focus:tw-ring-[#ACEDDA] disabled:tw-opacity-60"
                    >
                        Буцах
                    </button>}
                    
                </div>
            </section>
        </div>
    );
};

// ----------------------------------------------------
// ⚙️ MAIN SCREEN COMPONENT
// ----------------------------------------------------

const PlateScreen = ({ onNavigate }) => {
    const [plateNumber, setPlateNumber] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const navigate = useNavigate();
    const { parkId } = useParams();

    const handleModalClose = useCallback(() => {
        setIsModalOpen(false);
        setPlateNumber("");
        setSearchResults([]);
        setError("");
    }, []);

    const handleSearch = useCallback(
        async (prefix) => {
            setLoading(true);
            setError("");

            try {
                const results = await fetchPlatesByPrefix(prefix, parkId);
                setSearchResults(results);
                setIsModalOpen(true);
            } catch (err) {
                setSearchResults([]);
                setError(err?.message || "Хайлт хийхэд алдаа гарлаа. Дахин оролдоно уу.");
            } finally {
                setLoading(false);
            }
        },
        [parkId],
    );

    const handleNumpadInput = useCallback(
        (key) => {
            if (loading || isModalOpen) return;

            setPlateNumber((prev) => {
                if (key === "⌫") return prev.slice(0, -1);
                if (key === "x") return "";
                if (prev.length < 4 && /[0-9]/.test(key)) return prev + key;
                return prev;
            });
        },
        [loading, isModalOpen],
    );

    useEffect(() => {
        if (plateNumber.length === 4) {
            handleSearch(plateNumber);
        }
    }, [plateNumber, handleSearch]);

    // Physical keyboards are convenient for desktop kiosks; touch keypad still works.
    useEffect(() => {
        const onKeyDown = (event) => {
            if (loading || isModalOpen || event.ctrlKey || event.altKey || event.metaKey) return;
            const tag = event.target?.tagName?.toLowerCase?.();
            if (tag === "input" || tag === "textarea") return;
            if (/^[0-9]$/.test(event.key)) {
                event.preventDefault();
                handleNumpadInput(event.key);
            } else if (event.key === "Backspace") {
                event.preventDefault();
                handleNumpadInput("⌫");
            } else if (event.key === "Delete" || event.key === "Escape") {
                event.preventDefault();
                handleNumpadInput("x");
            }
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [loading, isModalOpen, handleNumpadInput]);

    const handlePlateSelect = (selectedRecord) => {
        if (selectedRecord.ambiguous) {
            window.alert("Ижил орсон цагтай хоёр бүртгэл байна. Ажилтнаар тулгуулна уу.");
            return;
        }
        setIsModalOpen(false);
        setPlateNumber("");
        const targetPath = `/parking/${parkId}`;
        // Price and Minu credentials are supplied only by the backend.
        // Selecting the full plate is explicit confirmation to create its Minu QR.
        // The payment screen still performs the authoritative server checks.
        const stateData = {
            recordId: selectedRecord.recordId,
            plate: selectedRecord.plate,
            autoPay: true,
        };
        if (navigate) navigate(targetPath, { state: stateData });
        else if (onNavigate) onNavigate(targetPath, stateData);
    };

    const renderKey = (key) => {
        const isDisabled = loading || isModalOpen;
        const isClear = key === "x";
        const isBackspace = key === "⌫";

        let content = key;
        let extraClass =
            "tw-border-[#E4ECEE] tw-bg-white tw-text-[#112C35] hover:tw-border-[#0FA57A] hover:tw-bg-[#F3FCF8]";

        if (isBackspace) {
            content = (
                <svg
                    viewBox="0 0 24 24"
                    className="tw-h-[21px] tw-w-[21px]"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.9"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <path d="M21 5H9l-6 7 6 7h12a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1Z" />
                    <path d="m11 9 6 6M17 9l-6 6" />
                </svg>
            );
            extraClass = "tw-border-[#143641] tw-bg-[#143641] tw-text-white hover:tw-bg-[#24505E]";
        } else if (isClear) {
            content = (
                <span className="tw-flex tw-items-center tw-gap-1.5">
                    <svg
                        viewBox="0 0 24 24"
                        className="tw-h-[15px] tw-w-[15px]"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                    >
                        <path d="M6 6l12 12M18 6L6 18" />
                    </svg>
                </span>
            );
            extraClass =
                "tw-border-[#E4ECEE] tw-bg-[#F1F5F6] tw-text-[#48606C] hover:tw-bg-[#E6EEF0]";
        }

        return (
            <button
                type="button"
                key={key}
                onClick={() => handleNumpadInput(key)}
                disabled={isDisabled}
                aria-label={isClear ? "Цэвэрлэх" : isBackspace ? "Нэг цифр устгах" : key}
                className={`tw-flex tw-h-[63px] min-[390px]:tw-h-[68px] tw-items-center tw-justify-center tw-rounded-[17px] tw-border tw-text-[27px] tw-font-bold tw-tabular-nums tw-shadow-[0_2px_7px_rgba(16,42,50,0.045)] tw-transition-all tw-duration-150 active:tw-scale-[0.96] active:tw-shadow-none focus:tw-outline-none focus:tw-ring-4 focus:tw-ring-[#B4EEDD] disabled:tw-cursor-not-allowed disabled:tw-opacity-40 ${extraClass}`}
            >
                {content}
            </button>
        );
    };

    return (
        <div
            className="tw-fixed tw-inset-0 tw-z-[99999] tw-overflow-y-auto tw-overscroll-contain tw-bg-[#F1F6F5]"
            style={{
                fontFamily: UI_FONT,
                backgroundImage: "radial-gradient(circle at 4% 0%, rgba(110,221,186,0.17), transparent 40%), radial-gradient(circle at 100% 100%, rgba(71,148,173,0.08), transparent 48%)",
            }}
        >
            <div className="tw-flex tw-min-h-full tw-items-center tw-justify-center tw-px-3 tw-py-5 sm:tw-py-9">
                <main className="tw-w-full tw-max-w-[430px]">
                    <section className="tw-overflow-hidden tw-rounded-[28px] tw-border tw-border-white tw-bg-white tw-shadow-[0_18px_65px_rgba(16,42,50,0.1)]">
                        <header className="tw-bg-[#102A35] tw-px-5 tw-pt-5 tw-pb-5 sm:tw-px-7">
                            <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
                                <img src={LOGO_URL} alt="iParking" className="tw-w-[130px] tw-object-contain" />
                                <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-border-white/15 tw-bg-white/10 tw-px-3 tw-py-1.5 tw-text-[10px] tw-font-semibold tw-text-[#E6FFF5]">
                                    <span className="tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-[#45E1AE]" />
                                    QR төлбөр
                                </span>
                            </div>
                            <div className="tw-mt-5 tw-flex tw-items-center tw-gap-2" aria-label="Алхам 1: Дугаар оруулах">
                                <span className="tw-h-[5px] tw-flex-1 tw-rounded-full tw-bg-[#4AE0B1]" />
                                <span className="tw-h-[5px] tw-flex-1 tw-rounded-full tw-bg-white/15" />
                                <span className="tw-h-[5px] tw-flex-1 tw-rounded-full tw-bg-white/15" />
                            </div>
                            <p className="tw-mt-3 tw-mb-0 tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-[0.14em] tw-text-[#7DE5BF]"> 1 / 3</p>
                            <h1 className="tw-mt-1 tw-mb-1 tw-text-center tw-text-[20px] tw-font-bold tw-leading-[1.2] tw-tracking-[-0.035em] tw-text-white">
                                Дугаарын 4 оронг оруулна уу
                            </h1>
                        
                        </header>

                        <div className="tw-px-5 tw-pt-5 tw-pb-5 sm:tw-px-7">
                            <div className="tw-mb-3 tw-flex tw-items-center tw-justify-between tw-gap-2">
                                <span className="tw-text-[11px] tw-font-semibold tw-tabular-nums tw-text-[#098869]">{plateNumber.length} / 4</span>
                            </div>
                            <div className="tw-grid tw-grid-cols-4 tw-gap-2.5" aria-label="Оруулсан улсын дугаарын цифрүүд">
                                {Array.from({ length: 4 }).map((_, index) => {
                                    const digit = plateNumber[index];
                                    const isActive = index === plateNumber.length && plateNumber.length < 4 && !loading && !isModalOpen;
                                    return (
                                        <div
                                            key={index}
                                            className={`tw-relative tw-flex tw-h-[71px] tw-items-center tw-justify-center tw-rounded-[17px] tw-border-2 tw-text-[35px] tw-font-extrabold tw-tabular-nums tw-transition-all tw-duration-150 ${digit
                                                ? "tw-border-[#0A8D69] tw-bg-[#EFFCF6] tw-text-[#143A3B]"
                                                : isActive
                                                    ? "tw-border-[#22B991] tw-bg-[#F8FEFB] tw-text-[#13A27C] tw-shadow-[0_0_0_3px_rgba(34,185,145,0.1)]"
                                                    : "tw-border-[#E5ECEE] tw-bg-[#F7F9FA] tw-text-slate-300"}`}
                                        >
                                            {digit || <span className={`tw-h-2 tw-w-2 tw-rounded-full ${isActive ? "tw-bg-[#22B991]" : "tw-bg-[#D2DCE0]"}`} />}
                                        </div>
                                    );
                                })}
                            </div>
                            <p className="tw-mt-3 tw-mb-4 tw-text-center tw-text-[11px] tw-leading-5 tw-text-slate-500">
                            </p>
                            <div className="tw-grid tw-grid-cols-3 tw-gap-2.5" aria-label="Тоон товчлуур">
                                {NUMPAD_KEYS.map(renderKey)}
                            </div>

                            <div aria-live="polite" className="tw-mt-4 tw-min-h-[30px]">
                                {loading && plateNumber.length === 4 && (
                                    <div className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-rounded-[13px] tw-bg-[#F1FBF7] tw-px-3 tw-py-2 tw-text-[12px] tw-font-semibold tw-text-[#087457]">
                                        <span className="tw-h-4 tw-w-4 tw-animate-spin tw-rounded-full tw-border-2 tw-border-[#BDE7D9] tw-border-t-[#078A66]" />
                                        Дугаар хайж байна...
                                    </div>
                                )}
                                {!loading && error && (
                                    <div role="alert" className="tw-rounded-[13px] tw-border tw-border-rose-200 tw-bg-rose-50 tw-px-3 tw-py-3 tw-text-center tw-text-[12px] tw-font-medium tw-text-rose-700">
                                        {error}
                                    </div>
                                )}
                            </div>
                        </div>
                    </section>
                    <p className="tw-mt-4 tw-mb-0 tw-text-center tw-text-[11px] tw-leading-5 tw-text-[#6B8290]">
                        iParking · Зогсоолын ухаалаг төлбөрийн систем
                    </p>
                </main>
            </div>

            {isModalOpen && (
                <PlateOptionsModal
                    plates={searchResults}
                    onSelect={handlePlateSelect}
                    onClose={handleModalClose}
                />
            )}
        </div>
    );
};

export default PlateScreen;
